import { LiquidGlass, Row, Stack, Toaster, TooltipProvider } from "@/ui";
import { ErrorBoundary } from '@/shared/components';
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import React, { lazy, Suspense, useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { I18nextProvider, useTranslation } from 'react-i18next';
import i18n from '@/shared/i18n/config';
import { trackPageview } from "@/shared/utils/analytics.ts";
import { trackPageView } from "@/shared/utils/mixpanel";
import { runStorageCleanup } from "@/shared/utils/storage-cleanup";
import { initializeOrderPersistence } from "@/shared/utils/order-persistence";
import { logger } from "@/shared/lib/logger/logger";
import { trackCoreWebVitals } from "@/shared/analytics/performance";
import SEO from "@/shared/components/SEO";

// Marketing features (global - needed immediately)
import { EmailCaptureModal } from "@/features/marketing";

// Country selection feature (global - needed on init)
import { CountryInitializer } from "@/features/country";

// Cart drawer (global component)
import { CartDrawer } from "@/features/cart";

// Critical path - keep static for LCP
import { LandingPage } from "@/features/landing";
import { queryClient } from "@/shared/lib/query-client";
import { isDevelopment } from "@/shared/config/environment";
import {
  prefetchOrdersByEmail,
  prefetchProducts,
  prefetchSupportedCountries,
  prefetchUserProfile
} from "@/shared/lib/query-prefetch";
import { authService } from "@/features/auth";
import { GlobalMutationIndicator } from "@/shared/components/GlobalMutationIndicator";

// ============================================================================
// LAZY-LOADED ROUTES - Code splitting for better initial load performance
// ============================================================================

// Pages - Static content pages
const NotFound = lazy(() => import("@/pages/NotFound.tsx"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy.tsx"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService.tsx"));
const FAQ = lazy(() => import("@/pages/FAQ.tsx"));
const AboutUs = lazy(() => import("@/pages/AboutUs.tsx"));
const CookiePolicy = lazy(() => import("@/pages/CookiesPolicy.tsx"));
const Contact = lazy(() => import("@/pages/Contact.tsx"));

// Features - Heavy feature pages
const WorkshopPage = lazy(() => import("@/features/workshop/WorkshopPage/WorkshopPage").then(module => ({ default: module.WorkshopPage })));
const ConversationalWorkshop = lazy(() => import("@/features/workshop/ConversationalWorkshop/ConversationalWorkshop").then(module => ({ default: module.ConversationalWorkshop })));
const DashboardPage = lazy(() => import("@/features/dashboard/DashboardPage"));
const AdminPage = lazy(() => import("@/features/admin/AdminPage"));
const StudioPage = lazy(() => import("@/features/studio/StudioPage"));
const CheckoutPage = lazy(() => import("@/features/checkout/CheckoutPage/CheckoutPage").then(module => ({ default: module.Checkout })));
const OrderConfirmationPage = lazy(() => import("@/features/checkout/OrderConfirmationPage").then(module => ({ default: module.OrderConfirmationPage })));

// ============================================================================
// PAGE LOADER - Suspense fallback for lazy routes
// ============================================================================

const PageLoader: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Row align="center" justify="center" className="min-h-screen bg-gradient-to-br from-background to-[hsl(var(--primary)/0.05)]">
      <LiquidGlass thickness="thin" rounded="xl" padding="lg" animate="appear" specular>
        <Stack align="center" gap={4}>
          <div className="h-8 w-8 motion-safe:animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-body-sm-responsive text-muted-foreground">{t('ui.pageloader.loading')}</p>
        </Stack>
      </LiquidGlass>
    </Row>
  );
};

function AnalyticsListener(): null {
  const location = useLocation();
  useEffect(() => {
    const path = `${location.pathname}${location.search || ''}`;
    // Track with Google Analytics
    trackPageview(path);
    // Track with Mixpanel
    trackPageView({
      path: location.pathname,
      search: location.search || '',
      referrer: document.referrer || '',
      title: document.title,
    });
  }, [location.pathname, location.search]);
  return null;
}

/**
 * Route-based Prefetching Component
 *
 * ✅ ROUTE-BASED PREFETCHING: Prefetches data based on current route
 * Improves perceived performance by loading data before user needs it
 */
function RoutePrefetcher(): null {
  const location = useLocation();

  useEffect(() => {
    const prefetchForRoute = async (): Promise<void> => {
      try {
        const pathname = location.pathname;

        // Prefetch products when navigating to workshop
        if (pathname.startsWith('/workshop')) {
          await prefetchProducts().catch(() => {
            // Silently fail - prefetching is just an optimization
          });
        }

        // Prefetch studio data when navigating to studio
        if (pathname === '/studio') {
          const isAuth = await authService.isAuthenticated();
          if (isAuth) {
            const user = authService.getCurrentUser();
            if (user) {
              // Firebase uses 'uid' for user ID
              const userId = 'uid' in user && typeof user.uid === 'string' ? user.uid : null;
              if (userId && typeof userId === 'string') {
                await prefetchUserProfile(userId).catch(() => {
                  // Silently fail - user might not have profile yet
                });
              }
            }
          }
        }

        // Prefetch dashboard data when navigating to dashboard
        if (pathname === '/dashboard') {
          const isAuth = await authService.isAuthenticated();
          if (isAuth) {
            const user = authService.getCurrentUser();
            if (user) {
              // Firebase uses 'uid' for user ID
              const userId = 'uid' in user && typeof user.uid === 'string' ? user.uid : null;
              const userEmail = 'email' in user ? user.email : null;

              if (userId && typeof userId === 'string') {
                await prefetchUserProfile(userId).catch(() => {
                  // Silently fail
                });
              }

              if (userEmail) {
                await prefetchOrdersByEmail(userEmail).catch(() => {
                  // Silently fail
                });
              }
            }
          }
        }
      } catch (error) {
        // Silently fail prefetching - it's just an optimization
        logger.debug('Route prefetching failed (non-critical)', {
          component: 'App',
          action: 'prefetch_routes',
          error: error instanceof Error ? error.message : String(error),
        });
      }
    };

    // Prefetch after a short delay to not block navigation
    const timeoutId = setTimeout(() => {
      void prefetchForRoute();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  return null;
}

const App = React.memo(function App(): React.JSX.Element {
  // ✅ PERSISTENCE: Initialize order persistence on page load
  useEffect(() => {
    // Initialize browser session detection
    initializeOrderPersistence();
  }, []);

  // Initialize storage cleanup - runs once on app mount
  useEffect(() => {
    runStorageCleanup();
  }, []);

  // ✅ PERFORMANCE: Track Core Web Vitals in Mixpanel for performance correlation
  useEffect(() => {
    trackCoreWebVitals();
  }, []);

  // ✅ PREFETCHING: Prefetch common data on app load for better UX
  useEffect(() => {
    const prefetchData = async (): Promise<void> => {
      try {
        // Prefetch products and supported countries (public data, always safe to prefetch)
        await Promise.allSettled([
          prefetchProducts(),
          prefetchSupportedCountries(),
        ]);

        // Prefetch user data if authenticated
        const isAuth = await authService.isAuthenticated();
        if (isAuth) {
          const user = authService.getCurrentUser();
          if (user) {
            // Firebase uses 'uid' for user ID
            const userId = 'uid' in user && typeof user.uid === 'string' ? user.uid : null;
            const userEmail = 'email' in user ? user.email : null;

            if (userId && typeof userId === 'string') {
              await prefetchUserProfile(userId).catch(() => {
                // Silently fail - user might not have profile yet
              });
            }

            if (userEmail) {
              await prefetchOrdersByEmail(userEmail).catch(() => {
                // Silently fail - might not have orders yet
              });
            }
          }
        }
      } catch (error) {
        // Silently fail prefetching - it's just an optimization
        logger.debug('Prefetching failed (non-critical)', {
          component: 'App',
          action: 'prefetch_data',
          error: error instanceof Error ? error.message : String(error),
        });
      }
    };

    // Prefetch after a short delay to not block initial render
    const timeoutId = setTimeout(() => {
      void prefetchData();
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          {/* ✅ COUNTRY SELECTION: Initialize country store on mount */}
          <CountryInitializer />
          <TooltipProvider>
            <Toaster />
            {/* ✅ V5 FEATURE: Global mutation indicator */}
            <GlobalMutationIndicator />
            <BrowserRouter>
              <SEO />
              <AnalyticsListener />
              <RoutePrefetcher />
              {/* Email Capture Modal - Exit intent popup for lead generation */}
              <EmailCaptureModal />
              {/* Cart Drawer - Global slide-out cart panel */}
              <CartDrawer />
              {isDevelopment() && <ReactQueryDevtools initialIsOpen={false} />}
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Critical path - Landing page is NOT lazy loaded for best LCP */}
                  <Route path="/" element={<LandingPage />} errorElement={<ErrorBoundary />} />

                  {/* Feature pages - Lazy loaded */}
                  <Route path="/dashboard" element={<DashboardPage />} errorElement={<ErrorBoundary />} />
                  <Route path="/admin" element={<AdminPage />} errorElement={<ErrorBoundary />} />

                  {/* Redirect legacy /create routes to the new Workshop */}
                  <Route path="/create" element={<Navigate to="/workshop" replace />} errorElement={<ErrorBoundary />} />
                  <Route path="/create/*" element={<Navigate to="/workshop" replace />} errorElement={<ErrorBoundary />} />
                  <Route path="/studio" element={<StudioPage />} errorElement={<ErrorBoundary />} />
                  <Route path="/order-confirmation" element={<OrderConfirmationPage />} errorElement={<ErrorBoundary />} />

                  {/* Static content pages - Lazy loaded */}
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} errorElement={<ErrorBoundary />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} errorElement={<ErrorBoundary />} />
                  <Route path="/cookie-policy" element={<CookiePolicy />} errorElement={<ErrorBoundary />} />
                  <Route path="/faq" element={<FAQ />} errorElement={<ErrorBoundary />} />
                  <Route path="/about" element={<AboutUs />} errorElement={<ErrorBoundary />} />
                  <Route path="/contact" element={<Contact />} errorElement={<ErrorBoundary />} />

                  {/* Workshop v2 Routes - The "Living Workshop" (heavy - includes Three.js) */}
                  <Route path="/workshop" element={<WorkshopPage />} errorElement={<ErrorBoundary />} />
                  <Route path="/workshop/:sessionId" element={<WorkshopPage />} errorElement={<ErrorBoundary />} />

                  {/* Chat Routes - Conversational AI Workshop */}
                  <Route path="/chat" element={<ConversationalWorkshop />} errorElement={<ErrorBoundary />} />
                  <Route path="/chat/:sessionId" element={<ConversationalWorkshop />} errorElement={<ErrorBoundary />} />

                  {/* Checkout Flow */}
                  <Route path="/checkout" element={<CheckoutPage />} errorElement={<ErrorBoundary />} />

                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} errorElement={<ErrorBoundary />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </I18nextProvider>
  );
});

export default App;
